/**
 * Analytics Configuration for Rely
 *
 * Uses PostHog for product analytics and user behavior tracking.
 * PostHog is privacy-friendly and provides funnel analysis, session recording,
 * and feature flags.
 */
import { buildAnalyticsContext, resolveTrafficOverride } from './analyticsContext';
import { readAcquisitionSession } from '../services/acquisitionSession';

// PostHog configuration
export const POSTHOG_CONFIG = {
  apiKey: process.env.REACT_APP_POSTHOG_KEY || '',
  apiHost: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
  options: {
    // Disable automatic event capture - we want manual control
    autocapture: false,
    // Capture page views automatically
    capture_pageview: true,
    // Capture page leaves for session duration
    capture_pageleave: true,
    // Store in localStorage for persistence
    persistence: 'localStorage',
    // Disable session recording by default (can enable later)
    disable_session_recording: true,
    // Respect Do Not Track browser setting
    respect_dnt: true,
    // Enable debug mode in development
    loaded: (posthog) => {
      if (typeof window !== 'undefined') {
        const acquisitionSessionId = readAcquisitionSession()?.acquisition_session_id;
        const trafficOverride = resolveTrafficOverride(
          window.location.search,
          process.env.REACT_APP_RELY_TRAFFIC_CLASS
        );
        posthog.register({
          ...buildAnalyticsContext(
            window.location.hostname,
            trafficOverride
          ),
          ...(acquisitionSessionId ? { acquisition_session_id: acquisitionSessionId } : {}),
        });
      }
      if (process.env.NODE_ENV === 'development') {
        // In development, you can enable debug mode
        // posthog.debug()
      }
    },
  },
};

// Event names for consistency across the app
export const ANALYTICS_EVENTS = {
  // Wizard events
  WIZARD_STARTED: 'wizard_started',
  PLAN_SELECTED: 'plan_selected',
  STEP_COMPLETED: 'step_completed',
  STEP_VIEWED: 'step_viewed',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETED: 'payment_completed',
  PAYMENT_FAILED: 'payment_failed',
  WIZARD_COMPLETED: 'wizard_completed',
  WIZARD_ABANDONED: 'wizard_abandoned',

  // Company events
  COMPANY_CREATED: 'company_created',
  COMPANY_VIEWED: 'company_viewed',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  DOCUMENT_UPLOADED: 'document_uploaded',
  EMPTY_STATE_ROUTE_SELECTED: 'empty_state_route_selected',
  IMPORT_ENTRY_SELECTED: 'import_entry_selected',
  IMPORT_RESUME_CLICKED: 'import_resume_clicked',
  IMPORT_STARTED: 'import_started',
  IMPORT_SUBMITTED: 'import_submitted',

  // Financial services events
  FINANCIAL_APP_STARTED: 'financial_app_started',
  FINANCIAL_APP_SUBMITTED: 'financial_app_submitted',

  // Support events
  SUPPORT_TICKET_OPENED: 'support_ticket_opened',
  HELP_CENTER_VIEWED: 'help_center_viewed',

  // User events
  USER_SIGNED_UP: 'user_signed_up',
  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_COMPLETED: 'registration_completed',
  FORMATION_STARTED: 'formation_started',
  CHECKOUT_STARTED: 'checkout_started',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  PROFILE_UPDATED: 'profile_updated',
};

// Check if analytics is enabled
export const isAnalyticsEnabled = () => {
  return Boolean(POSTHOG_CONFIG.apiKey) && process.env.NODE_ENV !== 'test';
};
